import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { IEmpresas } from '../../interfaces/IEmpresas';
import { IContratoLancamentoEmpresaRequest, IDashboardContratoLancamento } from '../../interfaces/IContratoLancamento';
import ContratoLancamentoService from '../../services/ContratoLancamentoService';
import EmpresasService from '../../services/EmpresasService';
import { MASCARA_DATA } from '../../utils/Constants';
import MaskedInput from 'react-text-mask';

interface Props {
    clienteId: number;
    ativo: boolean;
}

type Modalidade = 'CLIENTE' | 'EMPRESA';

function converterDataBr(dataBr: string): string {
    const partes = dataBr.split('/');
    if (partes.length !== 3) return '';
    return `${partes[2]}-${partes[1]}-${partes[0]}`;
}

function ClientesVolumesContratadosTab({ clienteId, ativo }: Props) {
    const [carregado, setCarregado] = useState<boolean>(false);
    const [dashboard, setDashboard] = useState<IDashboardContratoLancamento | null>(null);
    const [empresas, setEmpresas] = useState<IEmpresas[]>([]);
    const [salvando, setSalvando] = useState<boolean>(false);
    const [mostrarFormulario, setMostrarFormulario] = useState<boolean>(false);

    const [modalidade, setModalidade] = useState<Modalidade>('CLIENTE');
    const [quantidadeContratada, setQuantidadeContratada] = useState<string>('');
    const [quantidadePadraoPorEmpresa, setQuantidadePadraoPorEmpresa] = useState<string>('');
    const [dataContratacao, setDataContratacao] = useState<string>('');
    const [vigenciaInicio, setVigenciaInicio] = useState<string>('');
    const [vigenciaFim, setVigenciaFim] = useState<string>('');
    const [valorExcedenteUnitario, setValorExcedenteUnitario] = useState<string>('');
    const [renovacaoAutomatica, setRenovacaoAutomatica] = useState<boolean>(false);
    const [quantidadePorEmpresa, setQuantidadePorEmpresa] = useState<Record<number, string>>({});
    const [qtdsEditadas, setQtdsEditadas] = useState<Record<number, string>>({});
    const [salvandoEmpresa, setSalvandoEmpresa] = useState<Record<number, boolean>>({});
    const [cancelando, setCancelando] = useState<boolean>(false);

    useEffect(() => {
        if (!ativo) return;
        carregarDados();
        // eslint-disable-next-line
    }, [ativo, clienteId]);

    async function carregarDados() {
        setCarregado(false);

        const [dashboardData, empresasData] = await Promise.all([
            ContratoLancamentoService.obterDashboard(clienteId).catch(() => null),
            EmpresasService.obterTodosPorClienteID(clienteId).catch(() => []),
        ]);

        setDashboard(dashboardData);
        setEmpresas(empresasData);
        setMostrarFormulario(!dashboardData || dashboardData.status === 'SEM_CONTRATO');

        const qtds: Record<number, string> = {};
        dashboardData?.porEmpresa?.forEach((linha) => { qtds[linha.empresaID] = String(linha.quantidadeContratada); });
        setQtdsEditadas(qtds);

        setCarregado(true);
    }

    async function handleCancelarContrato() {
        const confirm = await Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-modal-excluir ms-5',
                cancelButton: 'btn btn-modal-cancelar'
            },
            buttonsStyling: false
        }).fire({
            title: 'Cancelar contrato',
            text: 'O contrato vigente será cancelado (o histórico de consumo e alertas já registrados é preservado). Deseja continuar?',
            showCancelButton: true,
            cancelButtonText: 'Voltar',
            confirmButtonText: 'Cancelar contrato',
            reverseButtons: true
        });

        if (!confirm.isConfirmed) return;

        setCancelando(true);
        try {
            await ContratoLancamentoService.cancelar(clienteId);
            Swal.fire({ icon: 'success', title: 'Contrato cancelado', timer: 1500, showConfirmButton: false });
            await carregarDados();
        } catch (e: any) {
            Swal.fire({ icon: 'error', title: 'Erro ao cancelar contrato', text: e?.response?.data?.message ?? e?.message });
        } finally {
            setCancelando(false);
        }
    }

    async function handleSalvarQuantidadeEmpresa(empresaId: number) {
        const novaQuantidade = Number(qtdsEditadas[empresaId]);
        if (!novaQuantidade || novaQuantidade <= 0) return;

        setSalvandoEmpresa((prev) => ({ ...prev, [empresaId]: true }));
        try {
            await ContratoLancamentoService.editarQuantidadeEmpresa(clienteId, empresaId, novaQuantidade);
            Swal.fire({ icon: 'success', title: 'Quantidade atualizada', timer: 1500, showConfirmButton: false });
            await carregarDados();
        } catch (e: any) {
            Swal.fire({ icon: 'error', title: 'Erro ao atualizar quantidade', text: e?.response?.data?.message ?? e?.message });
        } finally {
            setSalvandoEmpresa((prev) => ({ ...prev, [empresaId]: false }));
        }
    }

    const temContratoAtivo = !!dashboard && dashboard.status !== 'SEM_CONTRATO';

    const totalPorEmpresa = useMemo(() => {
        if (modalidade !== 'EMPRESA') return 0;
        return Object.values(quantidadePorEmpresa).reduce((acc, v) => acc + (Number(v) || 0), 0);
    }, [modalidade, quantidadePorEmpresa]);

    function aplicarQuantidadePadraoATodasEmpresas() {
        if (!quantidadePadraoPorEmpresa) return;
        const novo: Record<number, string> = {};
        empresas.forEach((empresa) => { novo[empresa.id] = quantidadePadraoPorEmpresa; });
        setQuantidadePorEmpresa(novo);
    }

    function formularioValido(): boolean {
        if (!dataContratacao || !vigenciaInicio) return false;
        if (modalidade === 'CLIENTE') return Number(quantidadeContratada) > 0;

        // Cliente sem nenhuma empresa importada ainda pode cadastrar só com a quantidade padrão —
        // ela é aplicada automaticamente em cada empresa conforme for sendo importada depois.
        const temEmpresaComQuantidade = empresas.some((empresa) => Number(quantidadePorEmpresa[empresa.id]) > 0);
        const temQuantidadePadrao = Number(quantidadePadraoPorEmpresa) > 0;
        return temEmpresaComQuantidade || temQuantidadePadrao;
    }

    async function handleSalvar() {
        if (!formularioValido()) return;

        setSalvando(true);
        try {
            const empresasRequest: IContratoLancamentoEmpresaRequest[] = modalidade === 'EMPRESA'
                ? empresas
                    .filter((empresa) => Number(quantidadePorEmpresa[empresa.id]) > 0)
                    .map((empresa) => ({
                        empresaID: empresa.id,
                        quantidadeContratada: Number(quantidadePorEmpresa[empresa.id]),
                    }))
                : [];

            await ContratoLancamentoService.criar(clienteId, {
                modalidade,
                dataContratacao: converterDataBr(dataContratacao),
                vigenciaInicio: converterDataBr(vigenciaInicio),
                vigenciaFim: vigenciaFim ? converterDataBr(vigenciaFim) : undefined,
                quantidadeContratada: modalidade === 'CLIENTE' ? Number(quantidadeContratada) : totalPorEmpresa,
                quantidadePadraoPorEmpresa: quantidadePadraoPorEmpresa ? Number(quantidadePadraoPorEmpresa) : undefined,
                valorExcedenteUnitario: Number((valorExcedenteUnitario || '0').replace(',', '.')),
                renovacaoAutomatica,
                empresas: empresasRequest,
            });

            Swal.fire({ icon: 'success', title: 'Contrato cadastrado com sucesso', timer: 2000, showConfirmButton: false });
            await carregarDados();
        } catch (e: any) {
            Swal.fire({ icon: 'error', title: 'Erro ao salvar contrato', text: e?.response?.data?.message ?? e?.message });
        } finally {
            setSalvando(false);
        }
    }

    return (
        <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '16px', minHeight: '300px', display: ativo ? 'block' : 'none' }}>
            {!carregado && <p style={{ color: '#A3A3A3', fontFamily: 'Merriweather Sans' }}>Carregando...</p>}

            {carregado && (
                <>
                    {temContratoAtivo && dashboard && (
                        <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                            <div style={{ fontWeight: 700, color: '#181C32', marginBottom: '10px' }}>
                                Contrato vigente — {dashboard.modalidade === 'EMPRESA' ? 'Por empresa' : 'Contabilidade interna'}
                            </div>
                            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '14px', color: '#4b5563' }}>
                                <span><strong>Contratado:</strong> {dashboard.quantidadeContratada.toLocaleString('pt-BR')}</span>
                                <span><strong>Utilizado:</strong> {dashboard.quantidadeUtilizada.toLocaleString('pt-BR')}</span>
                                <span><strong>Restante:</strong> {dashboard.quantidadeRestante.toLocaleString('pt-BR')}</span>
                                <span><strong>% Consumido:</strong> {dashboard.percentualConsumido.toString().replace('.', ',')}%</span>
                                {dashboard.quantidadeExcedente > 0 && (
                                    <span style={{ color: '#dc2626' }}>
                                        <strong>Excedente:</strong> {dashboard.quantidadeExcedente.toLocaleString('pt-BR')}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                                <button
                                    type='button'
                                    className='btn btn-sm btn-primario'
                                    onClick={() => setMostrarFormulario((prev) => !prev)}
                                >
                                    {mostrarFormulario ? 'Ocultar novo contrato' : 'Cadastrar novo contrato'}
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-sm btn-modal-excluir'
                                    disabled={cancelando}
                                    onClick={handleCancelarContrato}
                                >
                                    {cancelando ? (
                                        <span className='spinner-border spinner-border-sm' />
                                    ) : (
                                        'Cancelar contrato'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {temContratoAtivo && dashboard.modalidade === 'EMPRESA' && dashboard.porEmpresa.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontWeight: 700, color: '#181C32', marginBottom: '10px' }}>Volume por empresa</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
                                        <th style={{ textAlign: 'left', padding: '8px', fontSize: '14px', color: '#5E6397' }}>Empresa</th>
                                        <th style={{ padding: '8px', width: '110px', fontSize: '14px', color: '#5E6397' }}>Utilizado</th>
                                        <th style={{ padding: '8px', width: '160px', fontSize: '14px', color: '#5E6397' }}>Qtd. Contratada</th>
                                        <th style={{ padding: '8px', width: '80px', fontSize: '14px', color: '#5E6397', textAlign: 'center' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dashboard.porEmpresa.map((linha) => (
                                        <tr key={linha.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                            <td style={{ padding: '8px' }}>{linha.empresaNome ?? `Empresa #${linha.empresaID}`}</td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>{linha.quantidadeUtilizada.toLocaleString('pt-BR')}</td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    type='number'
                                                    min={1}
                                                    className='form-control form-control-sm'
                                                    value={qtdsEditadas[linha.empresaID] ?? linha.quantidadeContratada}
                                                    onChange={(e) =>
                                                        setQtdsEditadas((prev) => ({ ...prev, [linha.empresaID]: e.target.value }))
                                                    }
                                                />
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                {salvandoEmpresa[linha.empresaID]
                                                    ? <span className='spinner-border spinner-border-sm' style={{ color: '#5E6397' }} />
                                                    : (
                                                        <i
                                                            onClick={() => handleSalvarQuantidadeEmpresa(linha.empresaID)}
                                                            className='fas fa-save'
                                                            style={{ color: '#5E6397', cursor: 'pointer' }}
                                                            title='Salvar quantidade'
                                                        />
                                                    )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!temContratoAtivo && (
                        <p style={{ color: '#A3A3A3', fontFamily: 'Merriweather Sans', marginBottom: '16px' }}>
                            Nenhum contrato de volume de lançamentos cadastrado ainda pra esse cliente.
                        </p>
                    )}

                    {mostrarFormulario && (
                        <div>
                            <div style={{ marginBottom: '16px' }}>
                                <label className='form-label required'>Modalidade de contratação</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        type='button'
                                        className={`btn btn-filtro ${modalidade === 'CLIENTE' ? 'ativo' : ''}`}
                                        onClick={() => setModalidade('CLIENTE')}
                                    >
                                        Contabilidade Interna (volume único do cliente)
                                    </button>
                                    <button
                                        type='button'
                                        className={`btn btn-filtro ${modalidade === 'EMPRESA' ? 'ativo' : ''}`}
                                        onClick={() => setModalidade('EMPRESA')}
                                    >
                                        Por Empresa (volume individual por CNPJ)
                                    </button>
                                </div>
                            </div>

                            <div className='row g-3' style={{ marginBottom: '10px' }}>
                                <div className='col-md-3'>
                                    <label className='form-label required'>Data da Contratação</label>
                                    <MaskedInput
                                        mask={MASCARA_DATA}
                                        value={dataContratacao}
                                        onChange={(e) => setDataContratacao(e.target.value)}
                                        placeholder='00/00/0000'
                                        type='text'
                                        className='form-control'
                                    />
                                </div>
                                <div className='col-md-3'>
                                    <label className='form-label required'>Vigência Início</label>
                                    <MaskedInput
                                        mask={MASCARA_DATA}
                                        value={vigenciaInicio}
                                        onChange={(e) => setVigenciaInicio(e.target.value)}
                                        placeholder='00/00/0000'
                                        type='text'
                                        className='form-control'
                                    />
                                </div>
                                <div className='col-md-3'>
                                    <label className='form-label'>Vigência Fim (opcional)</label>
                                    <MaskedInput
                                        mask={MASCARA_DATA}
                                        value={vigenciaFim}
                                        onChange={(e) => setVigenciaFim(e.target.value)}
                                        placeholder='00/00/0000'
                                        type='text'
                                        className='form-control'
                                    />
                                </div>
                                <div className='col-md-3'>
                                    <label className='form-label required'>Valor do Excedente (por lançamento)</label>
                                    <input
                                        type='text'
                                        className='form-control'
                                        placeholder='0,00'
                                        value={valorExcedenteUnitario}
                                        onChange={(e) => setValorExcedenteUnitario(e.target.value)}
                                    />
                                </div>
                            </div>

                            {modalidade === 'CLIENTE' && (
                                <div className='row g-3' style={{ marginBottom: '10px' }}>
                                    <div className='col-md-4'>
                                        <label className='form-label required'>Quantidade Contratada (total)</label>
                                        <input
                                            type='number'
                                            min={1}
                                            className='form-control'
                                            value={quantidadeContratada}
                                            onChange={(e) => setQuantidadeContratada(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {modalidade === 'EMPRESA' && (
                                <div style={{ marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', marginBottom: '14px' }}>
                                        <div className='col-md-4'>
                                            <label className='form-label'>Quantidade padrão por empresa</label>
                                            <input
                                                type='number'
                                                min={1}
                                                className='form-control'
                                                placeholder='Ex.: 10000'
                                                value={quantidadePadraoPorEmpresa}
                                                onChange={(e) => setQuantidadePadraoPorEmpresa(e.target.value)}
                                            />
                                        </div>
                                        <button type='button' className='btn btn-sm btn-primario' onClick={aplicarQuantidadePadraoATodasEmpresas}>
                                            Aplicar a todas as empresas
                                        </button>
                                    </div>

                                    {empresas.length === 0 && (
                                        <p style={{ color: '#A3A3A3' }}>Esse cliente ainda não tem empresas cadastradas.</p>
                                    )}

                                    {empresas.length > 0 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
                                                    <th style={{ textAlign: 'left', padding: '8px', fontSize: '14px', color: '#5E6397' }}>Empresa</th>
                                                    <th style={{ padding: '8px', width: '200px', fontSize: '14px', color: '#5E6397' }}>Qtd. Contratada</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {empresas.map((empresa) => (
                                                    <tr key={empresa.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                        <td style={{ padding: '8px' }}>{empresa.nomeFantasia ?? empresa.razaoSocial}</td>
                                                        <td style={{ padding: '8px' }}>
                                                            <input
                                                                type='number'
                                                                min={0}
                                                                className='form-control form-control-sm'
                                                                value={quantidadePorEmpresa[empresa.id] ?? ''}
                                                                onChange={(e) =>
                                                                    setQuantidadePorEmpresa((prev) => ({ ...prev, [empresa.id]: e.target.value }))
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}

                                    <div style={{ marginTop: '10px', fontSize: '13px', color: '#5E6397' }}>
                                        Total contratado: <strong>{totalPorEmpresa.toLocaleString('pt-BR')}</strong>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginBottom: '16px' }}>
                                <div className='form-check'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        id='renovacaoAutomatica'
                                        checked={renovacaoAutomatica}
                                        onChange={(e) => setRenovacaoAutomatica(e.target.checked)}
                                    />
                                    <label className='form-check-label' htmlFor='renovacaoAutomatica'>
                                        Renovação automática ao final da vigência
                                    </label>
                                </div>
                            </div>

                            <button
                                type='button'
                                disabled={salvando || !formularioValido()}
                                onClick={handleSalvar}
                                className='btn btn-primario'
                            >
                                {salvando ? (
                                    <span className='indicator-progress' style={{ display: 'block' }}>
                                        Salvando...
                                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                    </span>
                                ) : (
                                    'Salvar Contrato'
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default ClientesVolumesContratadosTab;
